import { isFunction, isObject } from 'lodash'
import { FormatErrors } from '.'

/** @enum {string} */
export const ValidationFields = Object.freeze({
  ACRONYM: 'acronym',
  ABBREVIATION: 'abbreviation',
  CATEGORIES: 'categories',
  COLOR: 'color',
  COMMENT: 'comment',
  COUNTRY: 'country',
  DETAILS: 'details',
  EMAIL: 'email',
  EMPLACEMENT: 'emplacement',
  FIRSTNAME: 'firstname',
  GENDER: 'gender',
  INSTITUTIONS: 'institutions',
  INSTITUTION: 'institution',
  LANGUAGE: 'language',
  LASTNAME: 'lastname',
  MANAGED: 'managed',
  NAME: 'name',
  ORIGINAL_NAME: 'originalName',
  ROLES: 'roles',
  SCHEDULES: 'schedules',
  SCHOOL: 'school',
  SECTION: 'section',
  SECTIONS: 'sections',
  SECTION_TO_YEAR: 'sectionToYear',
  SECTOR: 'sector',
  REQUEST_ORIGIN: 'requestOrigin',
  REQUEST_PERIODS: 'requestPeriods',
  DURATION: 'duration',
  TYPE: 'type',
  TYPES: 'types',
  TOTAL: 'total',
  USERNAME: 'username',
  END_DATE: 'endDate',
  START_DATE: 'startDate',
  STUDENT: 'student',
  INTERNSHIP_YEAR: 'internshipYear',
  SCHOOL_YEAR: 'schoolYear',
  SECTORS: 'sectors',
  RESUME: 'resume',
  COVER_LETTER: 'cover_letter',
  OWNERSHIP: 'ownership',
  SPECIALITY: 'speciality',
  WORKING_REGIME: 'workingRegime',
  ASSISTANT: 'assistant'
})

/**
 * @param {object} validators
 * @param {string[]} selectedKeys
 */
export const selectValidators = (validators, selectedKeys) => selectedKeys.reduce((acc, selectedKey) => {
  if (!isFunction(validators[selectedKey])) {
    throw new Error(`selectValidators: argument \`selectedKeys\` contains at least one unsupported key (${selectedKey}).`)
  }

  acc[selectedKey] = validators[selectedKey]

  return acc
}, {})

const validatorsFactory = validatorName => {
  return VALIDATOR_LIST[validatorName ?? 'DEFAULT']
}

const DEFAULT_VALIDATOR = data => {
  return { isValid: true, errors: {} }
}

const isValidSection = (section) => {
  const isValid = section.name && section.schoolYearName

  return {
    isValid: isValid,
    errors: isValid
      ? {}
      : {
        sectionId: section.name ? null : 'Section name cannot be null.',
        schoolYearId: section.schoolYearName ? null : 'Section school year cannot be null.'
      }
  }
}

const isValidSchoolSpeciality = (schoolSpeciality) => {
  const isValid = schoolSpeciality.complete

  return {
    isValid: isValid,
    errors: isValid
      ? {}
      : {
        specialityId: schoolSpeciality.newSpeciality ? null : 'speciality cannot be null.',
        schoolYearId: schoolSpeciality.newStudyYear ? null : 'school year cannot be null.'
      }
  }
}

/**
 * @param {object} item
 * @param {Array<{key:string, validator: (any)=>boolean} | (any) => boolean>} validators
 */
export const isValid = (item, validators) => {
  const errors = {
    keys: [],
    messages: {}
  }

  validators.forEach(validator => {
    const config = isFunction(validator) ? { key: validator.name, validator } : validator
    const report = config.validator(item)

    if (!report) {
      errors.keys.push(config.key)
      errors.messages[config.key] = [FormatErrors.CANNOT_BE_EMPTY]
    } else if ((isObject(report) && !report.valid)) {
      errors.keys.push(config.key)
      errors.messages[config.key] = report.errors
    }
  })

  return errors
}

const VALIDATOR_LIST = {
  DEFAULT: DEFAULT_VALIDATOR,
  Section: isValidSection,
  SchoolSpeciality: isValidSchoolSpeciality
}

export {
  validatorsFactory,
  isValidSection,
  isValidSchoolSpeciality
}
